"use client";

import { HandSelector } from "@/components/ui/molecules/hand-selector";
import { useStudioEngagement } from "@/contexts/studio-context";
import { useSongListening } from "@/hooks/use-song-listening";
import type { SongControler } from "@/lib/track/controler/song-controler";

interface SongListeningBandProps {
  songControler: SongControler | null;
}

/**
 * Bande basse de la sidebar : ce que le morceau fait sonner.
 *
 * Placée dans la colonne « morceau » plutôt que dans la toolbar : la sidebar
 * est à largeur fixe et a déjà sa stratégie d'étroitesse (`panelsCompact`,
 * cf. `studio-frame`), là où la toolbar est une rangée unique sans repli.
 *
 * Ne rend rien dans deux cas :
 *  - le morceau ne porte pas les mains (`available`), MG/MD ne désigneraient
 *    rien ;
 *  - le parcours **par cœur** est à l'écran. Il pilote lui-même la voix du
 *    morceau passe après passe (`use-rhythm-run`), exactement comme il pilote
 *    le transport, que la toolbar masque pour la même raison. Un sélecteur
 *    affichant « Deux » pendant qu'une passe a coupé le morceau mentirait, et
 *    l'utilisateur pourrait le contredire en pleine passe.
 *
 *    Par cœur et pas « en parcours » : le parcours harmonique ne touche pas à
 *    la voix du morceau, donc le sélecteur y dit vrai et y reste.
 *
 * Au retour du parcours, l'état affiché redevient « Deux » — et c'est vrai :
 * `use-rhythm-run` repose `setSongVoice('all')` à chaque passe qui se solde et
 * à son démontage. Replier la sidebar ne démonte rien (`studio-frame` masque
 * le panneau sans le détruire), l'état choisi survit donc au repli.
 */
export function SongListeningBand({ songControler }: SongListeningBandProps) {
  const [engagement] = useStudioEngagement();
  const listening = useSongListening(songControler);

  if (!listening.available || engagement?.kind === "byHeart") {
    return null;
  }

  return (
    // 55 px au total (bordure + 6 + libellé 10 + 4 + contrôle 28 + 6), contre
    // les 174 px que prenait `TrackList` : le `SectionNavigator` récupère
    // 119 px nets. Plus serré supposerait de descendre le contrôle sous les
    // 28 px des autres commandes de l'app.
    <div className="shrink-0 border-t border-border-subtle px-2 pt-1.5 pb-1.5">
      <div className="pb-1 text-[10px] font-semibold uppercase tracking-[0.15em] leading-none text-accent-bright">
        Écouter
      </div>

      <HandSelector
        value={listening.mode}
        onChange={listening.setMode}
        withSilence
      />
    </div>
  );
}
